import {Mapper} from '@s-ui/domain'

const ADVERTISEMENTS_TYPES = ['ad_impression', 'ad_view']
const LIST_CONTEXT = 'list'
const ADVERTISEMENTS_CONTEXT = [
  LIST_CONTEXT,
  'dealer',
  'detail',
  'favoritesList',
  'modelDetail',
  'other',
  'packs',
  'visibilityProducts',
  'priceIndicator'
]

/**
 * @implements Mapper
 */
export default class FromPageTrackingJsonToAdvertisementsValueObjectMapper extends Mapper {
  /**
   * @constructor
   * @param {Object} param
   * @param {Object} param.config
   * @param {Object} param.valueObject
   */
  constructor({config, valueObject}) {
    super()

    const {PRICE_INDICATOR_LABEL_RANK} = config.get('priceIndicator')
    this._config = config
    this._highlightTypes = this._config.get('COMMON').HIGHLIGHT_TYPES
    this._priceIndicatorLabelRank = PRICE_INDICATOR_LABEL_RANK
    this._valueObject = valueObject
  }

  setParams({adsTypeName, context = LIST_CONTEXT, listType} = {}) {
    this.adsTypeName = ADVERTISEMENTS_TYPES.includes(adsTypeName) && adsTypeName
    this.context = ADVERTISEMENTS_CONTEXT.includes(context) && context
    this.listType = listType

    return this
  }

  /**
   * @param {Object} param
   * @param {Array} param.ads
   */
  map({ads = [], extendedProduct, filters} = {}) {
    const adsArray = ads.map(element => (typeof element === 'object' ? element : {id: +element}))
    const productsData = this._mapProducts({ads: adsArray, extendedProduct})
    const products = productsData.map(this._filterByContext)
    const result = this._valueObject({
      products,
      filteredProvinces: this._getFilteredProvinces({filters})
    })

    return result
  }

  _getFilteredProvinces = ({filters = {}}) => {
    const {provinces} = filters
    if (!provinces) return this._config.get('filteredProvincesResults').UNKNOWN
    return provinces
  }

  /**
   * @method
   * @private
   */
  _mapProducts = ({ads, extendedProduct}) =>
    ads.map((ad, index) => this._mapCurrentAd({ad, extendedProduct, index})).filter(Boolean)

  _getHighlightType = ({creationDate, publicationDate, isProfessional, isTop, isUrgent}) => {
    const isSubir = publicationDate !== creationDate
    if (isTop) return this._highlightTypes.top
    if (isSubir) return isProfessional ? this._highlightTypes.subir : this._highlightTypes.renueva
    if (isUrgent) return this._highlightTypes.urge
    return this._highlightTypes.notHighlighted
  }

  /**
   * @method
   * @private
   */
  _mapCurrentAd = ({ad, extendedProduct, index}) => {
    if (!ad.id) {
      this._warnBrokenAd()
      return
    }

    const currentAdListPosition = ad.list_position || index + 1
    const {priceDropData: {amount: priceDropAmount} = {}} = ad
    const PRICE_DROP_TRACKING_VALUE_DEFAULT = 0
    const priceDropTrackingValue = priceDropAmount > 0 ? priceDropAmount : PRICE_DROP_TRACKING_VALUE_DEFAULT

    const id = this.context === 'dealer' ? `dealer${ad.id}` : `${ad.id}`

    const hasPackage = Boolean(ad.pack)
    const hasListPageNumber = Boolean(ad.list_page_num)
    const hasPriceIndicator = Boolean(ad.priceRankIndicator)
    const hasReservation = Boolean(ad.hasReservation)
    const hasOnlineFinancing = Boolean(ad.hasOnlineFinancing)
    const hasPriceDrop = Boolean(ad.priceDrop)

    const currentAdData = {
      has_multiprovince:
        Boolean(ad?.location?.provinceIds?.length) && ad.location.provinceIds.length > 1 ? 'true' : 'false',
      category: this._getCategory(),
      id,
      ...(Boolean(currentAdListPosition) && {list_position: currentAdListPosition}),
      price: ad.price || 0,
      pricedrop: priceDropTrackingValue,
      quantity: ad.quantity || 0,
      ...(hasPackage && {package: ad.pack.type}),
      ...(hasListPageNumber && {list_page_num: ad.list_page_num}),
      ...(hasPriceIndicator && {
        price_indicator: this._priceIndicatorLabelRank[ad.priceRankIndicator]
      }),
      ...(hasReservation && {has_reservation: hasReservation}),
      ...(hasOnlineFinancing && {has_online_financing: hasOnlineFinancing}),
      has_pricedrop: hasPriceDrop,
      ...(ad.title && {title: ad.title}),
      ...(ad.make && {brand: ad.make}),
      ...(ad.model && {model: ad.model}),
      ...((ad.province || ad?.location?.mainProvince) && {province: ad.province || ad.location.mainProvince})
    }

    const highlightType = this._getHighlightType(ad)

    const hasVehicleHistoryData = typeof ad.vehicleHistory === 'boolean' ? {has_vehicle_history: ad.vehicleHistory} : {}

    const adTypeData = this.adsTypeName ? {[this.adsTypeName]: 1} : {}

    const listType = this.listType ? {list_type: this.listType} : {}

    return {
      ...currentAdData,
      ...adTypeData,
      ...hasVehicleHistoryData,
      highlight_type: highlightType,
      ...listType,
      ...(extendedProduct && extendedProduct.id === ad.id ? extendedProduct : {})
    }
  }

  /**
   * @method
   * @private
   */
  _getCategory = () => {
    switch (this.context) {
      case 'modelDetail':
        return 'OEM'
      case 'packs':
        return 'packs'
      case 'visibilityProducts':
        return 'visibility products'
      case 'dealer':
        return 'dealer'
      default:
        return 'ads'
    }
  }

  /**
   * @method
   * @private
   */
  _warnBrokenAd() {
    // eslint-disable-next-line
    console.warn(`[FromPageTrackingJsonToAdvertisementsValueObjectMapper] can't append ad without ID`)
  }

  /**
   * @method
   * @private
   */
  _filterByContext = product => {
    const allowedkeys = this._getContextProperties()

    return Object.keys(product)
      .filter(key => allowedkeys.includes(key))
      .reduce((obj, key) => {
        return {
          ...obj,
          [key]: product[key]
        }
      }, {})
  }

  /**
   * @method
   * @private
   */
  _getContextProperties = () => {
    switch (this.context) {
      case 'detail':
        return [
          'ad_view',
          'category',
          'has_multiprovince',
          'has_vehicle_history',
          'highlight_type',
          'id',
          'list_position',
          'list_type',
          'package',
          'price_indicator',
          'price',
          'pricedrop',
          'quantity',
          'title'
        ]
      case 'other':
      case 'packs':
      case 'visibilityProducts':
        return ['id', 'category', 'price', 'quantity']
      case 'favoritesList':
        return [
          'id',
          'category',
          'price',
          'quantity',
          'highlight_type',
          'list_position',
          'ad_impression',
          'list_type',
          'pricedrop',
          'price_indicator',
          'package'
        ]
      case 'dealer':
        return ['id', 'category', 'price', 'quantity', 'list_type']
      case 'priceIndicator':
        return ['id', 'price', 'quantity', 'price_indicator']
      default:
        return [
          'id',
          'category',
          'price',
          'quantity',
          'highlight_type',
          'list_position',
          'list_page_num',
          'price_indicator',
          'ad_impression',
          'list_type',
          'package',
          'has_multiprovince',
          'has_reservation',
          'title',
          'brand',
          'model',
          'province'
        ]
    }
  }
}
